import Button from '@CS/components/Button/Button.module.css';
import { useTranslation, useClassNames } from '@CS/hooks';
import { DELTA_UPDATE, clamp } from '@CS/utils/Number';
import { useState, useEffect, useRef } from 'react';
import type { TouchEvent } from 'react';
import CSS from './Weeks.module.css';
import Content from './content';

type LeaderBoardWeeksProps = {
  onClick: (index: number) => void;
  current?: number;
  weeks: number;
};

export const LeaderBoardWeeks = ({
  weeks,
  onClick,
  current = 0
}: LeaderBoardWeeksProps) => {
  const buttonWidth = useRef(75);
  const [end, setEnd] = useState(0);

  const [start, setStart] = useState(0);
  const [offset, setOffset] = useState(0);
  const { week } = useTranslation(Content);

  const container = useRef<HTMLElement>(null);
  const [position, setPosition] = useState(0);
  const [animate, setAnimate] = useState(false);

  const [selected, setSelected] = useState(current);
  const [scrollable, setScrollable] = useState(false);

  const containerClassNames = useClassNames([
    animate && CSS.animate,
    CSS.weeksContainer
  ]);

  const maxWidth =
    getComputedStyle(document.documentElement).getPropertyValue('--maxWidth') ||
    '480px';

  const weeksWidth =
    Math.min(window.innerWidth, +maxWidth.slice(0.0, -2.0)) * 0.9 -
    Math.min(window.innerHeight * 0.035, 25.0) * 2.0;

  const origin = (weeksWidth - buttonWidth.current) * 0.5;

  const setSelectedButton = (index: number) => {
    const position = index * (buttonWidth.current + 5);
    setPosition(origin - position);
    setEnd(origin - position);
  };

  const onTouchMove = (event: TouchEvent) => {
    if (!scrollable) return;
    const { clientX } = event.changedTouches[0];
    setPosition(clamp(clientX - start + end, offset, origin));
  };

  const onButtonClick = (index: number) => {
    const scrollDuration = +getComputedStyle(container.current!)
      .getPropertyValue('--scroll-duration')
      .slice(0.0, -2.0);

    setTimeout(() => setAnimate(false), scrollDuration);
    scrollable && setSelectedButton(index);

    setAnimate(scrollable);
    setSelected(index);
    onClick(index);
  };

  const onTouchStart = (event: TouchEvent) =>
    setStart(event.changedTouches[0].clientX);

  const onTouchEnd = () => setEnd(position);

  useEffect(() => {
    setTimeout(() => {
      const width = container.current?.offsetWidth ?? 0;
      const scrollable = width > weeksWidth;

      scrollable && setSelectedButton(selected);
      setOffset(weeksWidth - width - origin);
      setScrollable(scrollable);
    }, DELTA_UPDATE * 2);
  }, []);

  return (
    <nav
      style={{ transform: `translateX(${position}px)` }}
      className={containerClassNames}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      ref={container}
    >
      {[...Array(weeks)].map((_, w) => (
        <button
          key={w}
          onClick={onButtonClick.bind(null, w)}
          style={{
            minWidth: `${buttonWidth.current}px`,
            width: `${buttonWidth.current}px`
          }}
          className={useClassNames([
            selected === w && CSS.selected,
            Button.standard,
            CSS.weekButton
          ])}
        >
          {week} {w + 1}
        </button>
      ))}
    </nav>
  );
};
